import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import infopic from '../assets/icons/info-title.svg';
import defaultAvatar from '../assets/img/default-avatar.jpg';
import { getPaymentUrl, getSession, getUser } from '../services/Api';
import DataBlock from './DataBlock';
import GenderChart from './GenderChart'; // Импортируем GenderChart

import './Profile.css';

// Импорт иконок
import icon1 from '../assets/icons/icon-profile_1.svg';
import icon2 from '../assets/icons/icon-profile_2.svg';
import icon3 from '../assets/icons/icon-profile_3.svg';
import icon4 from '../assets/icons/icon-profile_4.svg';
import icon5 from '../assets/icons/icon-profile_5.svg';
import icon6 from '../assets/icons/icon-profile_6.svg';
import iconInfo from '../assets/icons/info.svg';
import ProfileSkeleton from './ProfileSkeleton';

// Регистрируем компоненты Chart.js
ChartJS.register(ArcElement, Tooltip, Legend);

const Profile = () => {
  const [userData, setUserData] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubscriptionActive, setIsSubscriptionActive] = useState(true);
  const [isAnalysisStarted, setIsAnalysisStarted] = useState(false);
  const [isFreeAnalysis, setIsFreeAnalysis] = useState(false);
  const [genderCounts, setGenderCounts] = useState({ maleCount: 0, femaleCount: 0 });
  const [activeTab, setActiveTab] = useState('likes');
  const [sentActiveTab, setSentActiveTab] = useState('sent_likes');
  const [giftsActiveTab, setGiftsActiveTab] = useState('received_gifts');
  const [loadingSection, setLoadingSection] = useState(null);
  const [paymentLink, setPaymentLink] = useState('');

  const navigate = useNavigate(); // Инициализируем navigate

  const handleAnalyzeAnotherProfile = () => {
    console.log('Переход на главную страницу');
    navigate('/', { state: { hideBlocks: true } });
  };

  // Функция для запуска полного анализа
  const activateFullAnalysis = async () => {
    console.log('Полный анализ запущен');
    return { success: true }; // Заглушка для примера
  };

  const fetchPaymentLink = async () => {
    try {
      const url = await getPaymentUrl();
      setPaymentLink(url);
    } catch (err) {
      setError(err.message || 'Ошибка при загрузке ссылки оплаты');
    }
  };

  useEffect(() => {
    fetchPaymentLink();

    const intervalId = setInterval(() => {
      fetchPaymentLink();
    }, 150000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    let timeoutId;
    const abortController = new AbortController();
    let isMounted = true;
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const fetchData = async () => {
      console.log('Начало загрузки данных...');

      try {
        const user = await getUser({ signal: abortController.signal });
        if (!isMounted) return;

        console.log('Данные пользователя загружены:', user);
        setUserData(user);

        const analyzeHistory = user.analyzeHistory;
        const lastProfileId = analyzeHistory[analyzeHistory.length - 1];

        const checkSessionStatus = async () => {
          try {
            const session = await getSession(lastProfileId, { signal: abortController.signal });
            if (!isMounted) return;

            if (session.success === false) {
              return handleAnalyzeAnotherProfile();
            }

            console.log('Данные сессии загружены:', session);
            setSessionData(session);

            if (session.data?.friendsAnalysis) {
              const maleCount = parseFloat(session.data.friendsAnalysis.maleRatio);
              const femaleCount = parseFloat(session.data.friendsAnalysis.femaleRatio);
              setGenderCounts({ maleCount, femaleCount });
            }

            // Проверяем, все ли статусы "completed"
            const isSessionComplete =
              session.data.status === 'completed' &&
              session.data.hiddenFriends?.status === 'completed' &&
              session.data.interactionAnalyses?.every(item => item.status === 'completed');

            if (isSessionComplete) {
              console.log('Все данные завершены');
              return; // Останавливаем пуллинг
            } else {
              timeoutId = setTimeout(checkSessionStatus, 1000); // Повторяем через 1 сек
            }
          } catch (error) {
            if (isMounted && error.name !== 'AbortError') {
              console.error('Ошибка при получении сессии', error);
            }
          }
        };

        checkSessionStatus();
      } catch (error) {
        if (isMounted && error.name !== 'AbortError') {
          console.error('Ошибка при загрузке данных пользователя или сессии', error);
          setError('Ошибка при загрузке данных пользователя или сессии');
        }
      } finally {
        if (isMounted) {
          console.log('Загрузка данных завершена');
          setTimeout(() => {
            setLoading(false);
          }, 2000);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      abortController.abort();
    };
  }, []);

  const chartData = React.useMemo(
    () => ({
      maleCount: genderCounts.maleCount,
      femaleCount: genderCounts.femaleCount
    }),
    [genderCounts.maleCount, genderCounts.femaleCount]
  );

  const handleActivateClick = async sectionId => {
    setLoadingSection(sectionId); // Устанавливаем ID секции, которая загружается
    setError(null);

    try {
      const paymentUrl = await getPaymentUrl();
      const link = document.createElement('a');
      link.href = paymentUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer'; // Безопасность
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingSection(null);
    }
  };

  const handleFullAnalysis = async () => {
    setIsAnalysisStarted(true); // Устанавливаем, что анализ запущен
    try {
      // Вызов API для запуска полного анализа
      const response = await activateFullAnalysis(); // Замените на ваш метод
      console.log('Полный анализ запущен:', response);
    } catch (error) {
      console.error('Ошибка при запуске анализа:', error);
    } finally {
      setIsAnalysisStarted(false); // Сбрасываем состояние после завершения
    }
  };

  if (error) {
    return <div>{error}</div>;
  }

  if (loading) {
    return <ProfileSkeleton />; // Используем скелетон во время загрузки
  }

  const profileStats = sessionData.data.profileStatistics;
  const profileInfo = sessionData.data.profileInfo;
  const interactionAnalyses = sessionData.data.interactionAnalyses || [];
  const hiddenFriends = sessionData.data.hiddenFriends;

  return (
    <div className="profile">
      {/* Существующий заголовок профиля */}
      <section className="profile__header">
        <div className="profile__greeting">
          <p className="profile__greeting-text">Добрый день,</p>
          <p className="profile__greeting-name">{userData?.name || 'Пользователь'}!</p>
        </div>
        <div className="profile__actions">
          {/* <button className="profile__button">Повторить анализ</button> */}
          <button className="profile__button" onClick={handleAnalyzeAnotherProfile}>
            Анализ другого профиля
          </button>
        </div>
      </section>

      {/* Блоки статистики */}
      <section className="profile__header">
        <h2 className="profile__section-title-prof">Результат последнего анализа:</h2>
        <div className="profile_header-bloc">
          <div className="profile__photo">
            <img
              id="profile-photo"
              src={profileInfo.photoUrl}
              alt={`${profileInfo.firstName} ${profileInfo.lastName}`}
            />
          </div>
          <div className="profile__name">
            <h1 id="first-name">{profileInfo.firstName}</h1>
            <h1 id="last-name">{profileInfo.lastName}</h1>
          </div>
        </div>
        <div className="profile__stats">
          <div className="profile__stat">
            <p className="profile__stat-label">Посты</p>
            <p id="posts-count" className="profile__stat-value">
              {profileStats.postsCount}
            </p>
          </div>
          <div className="profile__stat">
            <p className="profile__stat-label">Подписчики</p>
            <p id="subscribers-count" className="profile__stat-value">
              {profileStats.subscribersCount}
            </p>
          </div>
          <div className="profile__stat">
            <p className="profile__stat-label">Друзья</p>
            <p id="friends-count" className="profile__stat-value">
              {profileStats.friendsCount}
            </p>
          </div>
        </div>
      </section>

      <section class="profile__analysis">
        <div class="profile__activity">
          <div class="profile__activity-column">
            <div class="profile__activity-item">
              <a href="#" class="info">
                <img src={iconInfo} alt="" class="info-label" />
              </a>
              <img src={icon1} alt="" />
              <p class="profile__activity-label">Лайки</p>
              <p id="likes-count" class="profile__activity-value">
                {profileStats.likesCount}
              </p>
            </div>
            <div class="profile__activity-item">
              <a href="#" class="info">
                <img src={iconInfo} alt="" class="info-label" />
              </a>
              <img src={icon2} alt="" />
              <p class="profile__activity-label">Посты</p>
              <p id="posts-count-analysis" class="profile__activity-value">
                {profileStats.postsCount}
              </p>
            </div>
            <div class="profile__activity-item">
              <a href="#" class="info">
                <img src={iconInfo} alt="" class="info-label" />
              </a>
              <img src={icon3} alt="" />
              <p class="profile__activity-label">Просмотры</p>
              <p class="profile__activity-value">{profileStats.viewsCount}</p>
            </div>
            <div class="profile__activity-item">
              <a href="#" class="info">
                <img src={iconInfo} alt="" class="info-label" />
              </a>
              <img src={icon4} alt="" />
              <p class="profile__activity-label">Комментарии</p>
              <p class="profile__activity-value">{profileStats.commentsCount}</p>
            </div>
            <div class="profile__activity-item">
              <a href="#" class="info">
                <img src={iconInfo} alt="" class="info-label" />
              </a>
              <img src={icon5} alt="" />
              <p class="profile__activity-label">Подарки</p>
              <p class="profile__activity-value">{profileStats.giftsCount}</p>
            </div>
            <div class="profile__activity-item">
              <a href="#" class="info">
                <img src={iconInfo} alt="" class="info-label" />
              </a>
              <img src={icon6} alt="" />
              <p class="profile__activity-label">Репосты</p>
              <p class="profile__activity-value">{profileStats.repostsCount}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="wrapper-block">
        {(!userData || userData?.subExpiresAt < Math.floor(Date.now() / 1000)) && (
          <section className="data-block-section">
            <h2 className="profile__section-title">
              <img src={infopic} alt="ino" />
              Предполагаемые гости страницы
            </h2>
            <div className="data-container">
              <ul className="profile__likes-list fake_list">
                {[...Array(5)].map((_, index) => (
                  <li key={index} className="profile__likes-item">
                    <img src={defaultAvatar} alt="Фото" className="profile__hidden-friend-photo" loading="lazy" />
                    <span className="fake-data-name">Имя Фамилия</span>
                  </li>
                ))}
              </ul>
              <div className="zaglushka">
                <div className="text-blur">
                  Мы разработали алгоритм,
                  <br />
                  который вычислит предполагаемых гостей
                  <br />
                  анализируемого профиля!
                </div>
                <a
                  href={paymentLink || '#'}
                  id="payment-link"
                  className="btn-blur"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {paymentLink ? 'Получить анализ' : 'Загрузка...'}
                </a>
              </div>
            </div>
          </section>
        )}

        {(!userData || userData?.subExpiresAt < Math.floor(Date.now() / 1000)) && (
          <section className="data-block-section">
            <h2 className="profile__section-title">
              <img src={infopic} alt="ino" />
              Утекшие переписки
            </h2>
            <div className="data-container">
              <ul className="profile__likes-list fake_list">
                {[...Array(5)].map((_, index) => (
                  <li key={index} className="profile__likes-item">
                    <img src={defaultAvatar} alt="Фото" className="profile__hidden-friend-photo" loading="lazy" />
                    <span className="fake-data-name">Имя Фамилия</span>
                  </li>
                ))}
              </ul>
              <div className="zaglushka">
                <div className="text-blur">
                  Проверь утекшие переписки
                  <br />
                  всего за 14 руб!
                </div>
                <a
                  href={paymentLink || '#'}
                  id="payment-link"
                  className="btn-blur"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {paymentLink ? 'Проверить' : 'Загрузка...'}
                </a>
              </div>
            </div>
          </section>
        )}

        {/* Блок для скрытых друзей */}
        <section className="data-block-section">
          <h2 className="profile__section-title">
            <img src={infopic} alt="ino" />
            Предполагаемые скрытые друзья
          </h2>
          <div className="data-container">
            {/* Проверка на активную подписку */}
            {userData?.subExpiresAt > Math.floor(Date.now() / 1000) ? ( // Если подписка активна
              !isFreeAnalysis ? ( // И это не бесплатный анализ
                <>
                  {/* Проверка статуса hiddenFriends */}
                  {!sessionData?.data?.hiddenFriends ? ( // Если hiddenFriends не загружены
                    isAnalysisStarted ? ( // Если анализ запущен
                      <div className="zaglushka">
                        <div className="loading-spinner"></div>
                        <div className="loading-text">Информация обновляется</div>
                      </div>
                    ) : (
                      <div className="zaglushka">
                        <div className="text-blur">
                          Данных для анализа пока нет.
                          <br />
                          Запустите полный анализ, чтобы увидеть статистику.
                        </div>
                        <button
                          id="full-analysis-button"
                          className="btn-blur"
                          onClick={handleFullAnalysis} // Обработчик для запуска полного анализа
                        >
                          Запустить полный анализ
                        </button>
                      </div>
                    )
                  ) : sessionData.data.hiddenFriends.status === 'processing' ? ( // Если данные обновляются
                    <div className="zaglushka">
                      <div className="loading-spinner"></div>
                      <div className="loading-text">Информация обновляется</div>
                    </div>
                  ) : sessionData.data.hiddenFriends.users.length === 0 ? ( // Если данных нет
                    <div className="no-data">Данных нет</div>
                  ) : (
                    // Если данные загружены
                    <ul className="profile__likes-list profile__likes">
                      {sessionData.data.hiddenFriends.users
                        .filter(user => user.firstName !== 'DELETED') // Фильтруем удаленные аккаунты
                        .map(user => (
                          <li key={user.id} className="profile__likes-item">
                            <div className="like-item-photo-container">
                              <img
                                src={user.photoUrl}
                                alt={`${user.firstName} ${user.lastName}`}
                                className="like-item-photo"
                                loading="lazy"
                              />
                            </div>
                            <div className="like-item-text">
                              <a href={`https://vk.com/id${user.id}`} target="_blank" rel="noopener noreferrer">
                                {user.firstName} {user.lastName}
                              </a>
                            </div>
                          </li>
                        ))}
                    </ul>
                  )}
                </>
              ) : (
                // Блок для бесплатного анализа
                <div className="zaglushka">
                  <div className="text-blur">
                    Данных для анализа пока нет.
                    <br />
                    Запустите полный анализ, чтобы увидеть статистику.
                  </div>
                  <button
                    id="full-analysis-button"
                    className="btn-blur"
                    onClick={handleFullAnalysis} // Обработчик для запуска полного анализа
                  >
                    Запустить полный анализ
                  </button>
                </div>
              )
            ) : (
              // Если подписка неактивна, показываем плашку для активации
              <>
                <ul className="profile__likes-list fake_list">
                  {[...Array(5)].map((_, index) => (
                    <li key={index} className="profile__likes-item">
                      <img src={defaultAvatar} alt="Фото" className="profile__hidden-friend-photo" loading="lazy" />
                      <span className="fake-data-name">Имя Фамилия</span>
                    </li>
                  ))}
                </ul>
                <div className="zaglushka">
                  <div className="text-blur">
                    Активируйте свой профиль за 14 руб, чтобы узнать кого скрывает пользователь
                  </div>
                  <a
                    href={paymentLink || '#'}
                    id="payment-link"
                    className="btn-blur"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {paymentLink ? 'Узнать' : 'Загрузка...'}
                  </a>
                </div>
              </>
            )}
          </div>
        </section>

        {/* Отправленные лайки и комментарии */}
        <section className="data-block-section">
          <h2 className="profile__section-title">
            <img src={infopic} alt="ino" />
            Отправленные лайки и комментарии
          </h2>
          <div className="data-container">
            {/* Проверка на активную подписку */}
            {userData?.subExpiresAt > Math.floor(Date.now() / 1000) ? ( // Если подписка активна
              !isFreeAnalysis ? ( // И это не бесплатный анализ
                <div className="tabs-container">
                  <div className="tabs">
                    <button
                      className={sentActiveTab === 'sent_likes' ? 'tab-active' : 'tab'}
                      onClick={() => setSentActiveTab('sent_likes')}
                    >
                      Лайки
                    </button>
                    <button
                      className={sentActiveTab === 'sent_comments' ? 'tab-active' : 'tab'}
                      onClick={() => setSentActiveTab('sent_comments')}
                    >
                      Комментарии
                    </button>
                  </div>

                  {/* Проверка статуса для лайков и комментариев */}
                  {!interactionAnalyses.some(
                    analysis =>
                      (analysis.interactionType === 'likes_sent' || analysis.interactionType === 'comments_sent') &&
                      analysis.status === 'completed'
                  ) ? ( // Если данные о лайках или комментариях не обработаны
                    <div className="zaglushka">
                      <div className="loading-spinner"></div>
                      <div className="loading-text">Информация обновляется</div>
                    </div>
                  ) : interactionAnalyses.length === 0 ? ( // Если данных нет и анализ не запущен
                    <div className="zaglushka">
                      <div className="text-blur">
                        Данных для анализа пока нет.
                        <br />
                        Запустите полный анализ, чтобы увидеть статистику.
                      </div>
                      <button
                        id="full-analysis-button"
                        className="btn-blur"
                        onClick={handleFullAnalysis} // Обработчик для запуска полного анализа
                      >
                        Запустить полный анализ
                      </button>
                    </div>
                  ) : (
                    <>
                      {/* Вкладка отправленных лайков */}
                      {sentActiveTab === 'sent_likes' &&
                        interactionAnalyses
                          .filter(analysis => analysis.interactionType === 'likes_sent')
                          .map(analysis => (
                            <div key={analysis.id} className="tab-content">
                              {analysis.users.length > 0 ? (
                                analysis.users.map(user => (
                                  <ul key={user.id}>
                                    {user.items.slice(0, 3).map(item => (
                                      <li key={item.id} className="like-item">
                                        <div className="like-item-photo-container">
                                          <img
                                            src={user.photoUrl}
                                            alt={`${user.firstName} ${user.lastName}`}
                                            className="like-item-photo"
                                            loading="lazy"
                                          />
                                        </div>
                                        <div className="like-item-text">
                                          Лайк на{' '}
                                          {item.type === 'post' ? (
                                            <a
                                              href={`https://vk.com/wall${user.id}_${item.id}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                            >
                                              пост
                                            </a>
                                          ) : (
                                            <a
                                              href={`https://vk.com/photo${user.id}_${item.id}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                            >
                                              фото
                                            </a>
                                          )}{' '}
                                          для {user.firstName} {user.lastName}
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                ))
                              ) : (
                                <div className="no-data">Данных нет</div>
                              )}
                            </div>
                          ))}

                      {/* Вкладка отправленных комментариев */}
                      {sentActiveTab === 'sent_comments' &&
                        interactionAnalyses
                          .filter(analysis => analysis.interactionType === 'comments_sent')
                          .map(analysis => (
                            <div key={analysis.id} className="tab-content">
                              {analysis.users.length > 0 ? (
                                analysis.users.map(user => (
                                  <ul key={user.id}>
                                    {user.items.slice(0, 3).map(item => (
                                      <li key={item.id} className="comment-item">
                                        <div className="like-item-photo-container">
                                          <img
                                            src={user.photoUrl}
                                            alt={`${user.firstName} ${user.lastName}`}
                                            className="comment-item-photo"
                                            loading="lazy"
                                          />
                                        </div>
                                        <div className="like-item-text">
                                          Комментарий на{' '}
                                          {item.type === 'post' ? (
                                            <a
                                              href={`https://vk.com/wall${user.id}_${item.id}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                            >
                                              пост
                                            </a>
                                          ) : (
                                            <a
                                              href={`https://vk.com/photo${user.id}_${item.id}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                            >
                                              фото
                                            </a>
                                          )}{' '}
                                          для {user.firstName} {user.lastName}
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                ))
                              ) : (
                                <div className="no-data">Данных нет</div>
                              )}
                            </div>
                          ))}
                    </>
                  )}
                </div>
              ) : (
                // Блок для бесплатного анализа
                <div className="zaglushka">
                  <div className="text-blur">
                    Данных для анализа пока нет.
                    <br />
                    Запустите полный анализ, чтобы увидеть статистику.
                  </div>
                  <button
                    id="full-analysis-button"
                    className="btn-blur"
                    onClick={handleFullAnalysis} // Обработчик для запуска полного анализа
                  >
                    Запустить полный анализ
                  </button>
                </div>
              )
            ) : (
              // Если подписка неактивна, показываем плашку для активации
              <>
                <ul className="profile__likes-list fake_list">
                  {[...Array(5)].map((_, index) => (
                    <li key={index} className="profile__likes-item">
                      <img src={defaultAvatar} alt="Фото" className="profile__hidden-friend-photo" loading="lazy" />
                      <span className="fake-data-name">Имя Фамилия</span>
                    </li>
                  ))}
                </ul>
                <div className="zaglushka">
                  <div className="text-blur">
                    Активируйте свой профиль за 14 руб, чтобы узнать какие аккаунты лайкает и коментирует профиль в
                    тайне от тебя.
                  </div>
                  <a
                    href={paymentLink || '#'}
                    id="payment-link"
                    className="btn-blur"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {paymentLink ? 'Активировать' : 'Загрузка...'}
                  </a>
                </div>
              </>
            )}
          </div>
        </section>

        {/* Полученные лайки и комментарии */}
        <section className="data-block-section">
          <h2 className="profile__section-title">
            <img src={infopic} alt="ino" loading="lazy" />
            Полученные лайки и комментарии
          </h2>
          <div className="data-container">
            {/* Проверка на активную подписку */}
            {userData?.subExpiresAt > Math.floor(Date.now() / 1000) ? (
              !isFreeAnalysis ? ( // И это не бесплатный анализ
                <div className="tabs-container">
                  <div className="tabs">
                    <button
                      className={activeTab === 'likes' ? 'tab-active' : 'tab'}
                      onClick={() => setActiveTab('likes')}
                    >
                      Лайки
                    </button>
                    <button
                      className={activeTab === 'comments' ? 'tab-active' : 'tab'}
                      onClick={() => setActiveTab('comments')}
                    >
                      Комментарии
                    </button>
                  </div>

                  {/* Проверка статуса для лайков и комментариев */}
                  {!interactionAnalyses.some(
                    analysis =>
                      (analysis.interactionType === 'likes_received' ||
                        analysis.interactionType === 'comments_received') &&
                      analysis.status === 'completed'
                  ) ? (
                    <div className="zaglushka">
                      <div className="loading-spinner"></div>
                      <div className="loading-text">Информация обновляется</div>
                    </div>
                  ) : interactionAnalyses.length === 0 ? (
                    <div className="zaglushka">
                      <div className="text-blur">
                        Данных для анализа пока нет.
                        <br />
                        Запустите полный анализ, чтобы увидеть статистику.
                      </div>
                      <button
                        id="full-analysis-button"
                        className="btn-blur"
                        onClick={handleFullAnalysis} // Обработчик для запуска полного анализа
                      >
                        Запустить полный анализ
                      </button>
                    </div>
                  ) : (
                    <>
                      {/* Вкладка лайков */}
                      {activeTab === 'likes' &&
                        interactionAnalyses
                          .filter(analysis => analysis.interactionType === 'likes_received')
                          .map(analysis => (
                            <div key={analysis.id} className="tab-content">
                              {analysis.users.length > 0 ? (
                                analysis.users.map(user => (
                                  <ul key={user.id}>
                                    {user.items.slice(0, 3).map(item => (
                                      <li key={item.id} className="like-item">
                                        <div className="like-item-photo-container">
                                          <img
                                            src={user.photoUrl}
                                            alt={`${user.firstName} ${user.lastName}`}
                                            className="like-item-photo"
                                            loading="lazy"
                                          />
                                        </div>
                                        <div className="like-item-text">
                                          Лайк на{' '}
                                          {item.type === 'post' ? (
                                            <a
                                              href={`https://vk.com/wall${sessionData.data.analyzedUserId}_${item.id}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                            >
                                              пост
                                            </a>
                                          ) : (
                                            <a
                                              href={`https://vk.com/photo${sessionData.data.analyzedUserId}_${item.id}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                            >
                                              фото
                                            </a>
                                          )}{' '}
                                          от {user.firstName} {user.lastName}
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                ))
                              ) : (
                                <div className="no-data">Данных нет</div>
                              )}
                            </div>
                          ))}
                      {/* Вкладка комментариев */}
                      {activeTab === 'comments' &&
                        interactionAnalyses
                          .filter(analysis => analysis.interactionType === 'comments_received')
                          .map(analysis => (
                            <div key={analysis.id} className="tab-content">
                              {analysis.users.length > 0 ? (
                                analysis.users.map(user => (
                                  <ul key={user.id}>
                                    {user.items.slice(0, 3).map(item => (
                                      <li key={item.id} className="comment-item">
                                        <div className="like-item-photo-container">
                                          <img
                                            src={user.photoUrl}
                                            alt={`${user.firstName} ${user.lastName}`}
                                            className="comment-item-photo"
                                            loading="lazy"
                                          />
                                        </div>
                                        <div className="like-item-text">
                                          Комментарий на{' '}
                                          {item.type === 'post' ? (
                                            <a
                                              href={`https://vk.com/wall${sessionData.data.analyzedUserId}_${item.id}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                            >
                                              пост
                                            </a>
                                          ) : (
                                            <a
                                              href={`https://vk.com/photo${sessionData.data.analyzedUserId}_${item.id}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                            >
                                              фото
                                            </a>
                                          )}{' '}
                                          от {user.firstName} {user.lastName}
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                ))
                              ) : (
                                <div className="no-data">Данных нет</div>
                              )}
                            </div>
                          ))}
                    </>
                  )}
                </div>
              ) : (
                // Блок для бесплатного анализа
                <div className="zaglushka">
                  <div className="text-blur">
                    Данных для анализа пока нет.
                    <br />
                    Запустите полный анализ, чтобы увидеть статистику.
                  </div>
                  <button
                    id="full-analysis-button"
                    className="btn-blur"
                    onClick={handleFullAnalysis} // Обработчик для запуска полного анализа
                  >
                    Запустить полный анализ
                  </button>
                </div>
              )
            ) : (
              // Если подписка неактивна, показываем плашку для активации
              <>
                <ul className="profile__likes-list fake_list">
                  {[...Array(5)].map((_, index) => (
                    <li key={index} className="profile__likes-item">
                      <img src={defaultAvatar} alt="Фото" className="profile__hidden-friend-photo" loading="lazy" />
                      <span className="fake-data-name">Имя Фамилия</span>
                    </li>
                  ))}
                </ul>
                <div className="zaglushka">
                  <div className="text-blur">
                    Активируйте свой профиль за 14 руб, чтобы узнать кто чаще всего лайкает или коментирует аккаунт!
                  </div>
                  <a
                    href={paymentLink || '#'}
                    id="payment-link"
                    className="btn-blur"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {paymentLink ? 'Получить анализ' : 'Загрузка...'}
                  </a>
                </div>
              </>
            )}
          </div>
        </section>

        {/* Подарки */}
        <section className="data-block-section">
          <h2 className="profile__section-title">
            <img src={infopic} alt="ino" />
            Подарки
          </h2>
          <div className="data-container">
            {/* Проверка на активную подписку */}
            {userData?.subExpiresAt > Math.floor(Date.now() / 1000) ? ( // Если подписка активна
              !isFreeAnalysis ? ( // И это не бесплатный анализ
                <div className="tabs-container">
                  <div className="tabs">
                    <button
                      className={giftsActiveTab === 'received_gifts' ? 'tab-active' : 'tab'}
                      onClick={() => setGiftsActiveTab('received_gifts')}
                    >
                      Полученные
                    </button>
                    <button
                      className={giftsActiveTab === 'sent_gifts' ? 'tab-active' : 'tab'}
                      onClick={() => setGiftsActiveTab('sent_gifts')}
                    >
                      Отправленные
                    </button>
                  </div>

                  {/* Проверка статуса для подарков */}
                  {!interactionAnalyses.some(
                    analysis =>
                      (analysis.interactionType === 'gifts_received' || analysis.interactionType === 'gifts_sent') &&
                      analysis.status === 'completed'
                  ) ? ( // Если данные о подарках не обработаны
                    <div className="zaglushka">
                      <div className="loading-spinner"></div>
                      <div className="loading-text">Информация обновляется</div>
                    </div>
                  ) : interactionAnalyses.length === 0 ? ( // Если данных нет и анализ не запущен
                    <div className="zaglushka">
                      <div className="text-blur">
                        Данных для анализа пока нет.
                        <br />
                        Запустите полный анализ, чтобы увидеть статистику.
                      </div>
                      <button
                        id="full-analysis-button"
                        className="btn-blur"
                        onClick={handleFullAnalysis} // Обработчик для запуска полного анализа
                      >
                        Запустить полный анализ
                      </button>
                    </div>
                  ) : (
                    // Отображение данных
                    <>
                      {/* Полученные подарки */}
                      {giftsActiveTab === 'received_gifts' &&
                        interactionAnalyses
                          .filter(analysis => analysis.interactionType === 'gifts_received')
                          .map(analysis => (
                            <div key={analysis.id} className="tab-content">
                              {analysis.users.length > 0 ? (
                                analysis.users
                                  .filter(user => user.firstName !== 'DELETED') // Фильтруем удаленные аккаунты
                                  .map(user => (
                                    <ul key={user.id}>
                                      {user.items.slice(0, 3).map(item => (
                                        <li key={item.id} className="like-item">
                                          <div className="like-item-photo-container">
                                            <img
                                              src={user.photoUrl}
                                              alt={`${user.firstName} ${user.lastName}`}
                                              className="like-item-photo"
                                              loading="lazy"
                                            />
                                          </div>
                                          <div className="like-item-text">
                                            Подарок от{' '}
                                            <a
                                              href={`https://vk.com/id${user.id}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                            >
                                              {user.firstName} {user.lastName}
                                            </a>
                                            {item.message && <p className="gift-message">Сообщение: {item.message}</p>}
                                          </div>
                                        </li>
                                      ))}
                                    </ul>
                                  ))
                              ) : (
                                <div className="no-data">Данных нет</div>
                              )}
                            </div>
                          ))}

                      {/* Отправленные подарки */}
                      {giftsActiveTab === 'sent_gifts' &&
                        interactionAnalyses
                          .filter(analysis => analysis.interactionType === 'gifts_sent')
                          .map(analysis => (
                            <div key={analysis.id} className="tab-content">
                              {analysis.users.length > 0 ? (
                                analysis.users
                                  .filter(user => user.firstName !== 'DELETED') // Фильтруем удаленные аккаунты
                                  .map(user => (
                                    <ul key={user.id}>
                                      {user.items.slice(0, 3).map(item => (
                                        <li key={item.id} className="like-item">
                                          <div className="like-item-photo-container">
                                            <img
                                              src={user.photoUrl}
                                              alt={`${user.firstName} ${user.lastName}`}
                                              className="like-item-photo"
                                              loading="lazy"
                                            />
                                          </div>
                                          <div className="like-item-text">
                                            Подарок для{' '}
                                            <a
                                              href={`https://vk.com/id${user.id}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                            >
                                              {user.firstName} {user.lastName}
                                            </a>
                                            {item.message && <p className="gift-message">Сообщение: {item.message}</p>}
                                          </div>
                                        </li>
                                      ))}
                                    </ul>
                                  ))
                              ) : (
                                <div className="no-data">Данных нет</div>
                              )}
                            </div>
                          ))}
                    </>
                  )}
                </div>
              ) : (
                // Блок для бесплатного анализа
                <div className="zaglushka">
                  <div className="text-blur">
                    Данных для анализа пока нет.
                    <br />
                    Запустите полный анализ, чтобы увидеть статистику.
                  </div>
                  <button
                    id="full-analysis-button"
                    className="btn-blur"
                    onClick={handleFullAnalysis} // Обработчик для запуска полного анализа
                  >
                    Запустить полный анализ
                  </button>
                </div>
              )
            ) : (
              // Если подписка неактивна, показываем плашку для активации
              <>
                <ul className="profile__likes-list fake_list">
                  {[...Array(5)].map((_, index) => (
                    <li key={index} className="profile__likes-item">
                      <img src={defaultAvatar} alt="Фото" className="profile__hidden-friend-photo" loading="lazy" />
                      <span className="fake-data-name">Имя Фамилия</span>
                    </li>
                  ))}
                </ul>
                <div className="zaglushka">
                  <div className="text-blur">
                    Активируйте профиль за 14 руб, чтобы получить информацию по полученным и отправленным подаркам!
                  </div>
                  <a
                    href={paymentLink || '#'}
                    id="payment-link"
                    className="btn-blur"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {paymentLink ? 'Активировать' : 'Загрузка...'}
                  </a>
                </div>
              </>
            )}
          </div>
        </section>

        {/* Блок для статистики по полу */}
        <section className="data-block-section">
          <h2 className="profile__section-title">
            <img src={infopic} alt="ino" />
            Статистика по полу
          </h2>
          <div className="grad-container">
            {userData?.subExpiresAt > Math.floor(Date.now() / 1000) ? ( // Проверка активной подписки
              chartData.maleCount + chartData.femaleCount > 0 ? ( // Проверка наличия данных
                <div style={{ width: '300px', margin: '0 auto' }}>
                  <GenderChart maleCount={chartData.maleCount} femaleCount={chartData.femaleCount} />
                </div>
              ) : (
                <div className="zaglushka">
                  <div className="text-blur">
                    Данных для анализа пока нет.
                    <br />
                    Запустите полный анализ, чтобы увидеть статистику.
                  </div>
                  <button
                    id="full-analysis-button"
                    className="btn-blur"
                    onClick={() => handleFullAnalysis()} // Обработчик для запуска полного анализа
                  >
                    Запустить полный анализ
                  </button>
                </div>
              )
            ) : (
              <>
                <div style={{ width: '300px', margin: '0 auto' }}>
                  <GenderChart maleCount={50} femaleCount={50} />
                </div>
                <div className="zaglushka">
                  <div className="text-blur">
                    Узнай, кого больше: девушек или парней в друзьях профиля. Детальный отчёт!
                  </div>
                  <a
                    href={paymentLink || '#'}
                    id="payment-link"
                    className="btn-blur"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {paymentLink ? 'Получить отчёт' : 'Загрузка...'}
                  </a>
                </div>
              </>
            )}
          </div>
        </section>
      </section>
    </div>
  );
};

export default Profile;
